/* eslint-disable @typescript-eslint/naming-convention */
import { Component, Input, OnChanges } from '@angular/core';
import Map from 'ol/Map';
import View from 'ol/View';
import VectorLayer from 'ol/layer/Vector';
import { Fill, Stroke, Style } from 'ol/style';
import Polyline from 'ol/format/Polyline';
import Feature from 'ol/Feature';
import VectorSource from 'ol/source/Vector';
import CircleStyle from 'ol/style/Circle';
import { fromLonLat } from 'ol/proj';
import LineString from 'ol/geom/LineString';
import TileLayer from 'ol/layer/Tile';
import XYZ from 'ol/source/XYZ';

type MapLayer =
  | 'default'
  | 'road'
  | 'satellite'
  | 'hybrid'
  | 'terrain'
  | 'dark'
  | 'light';

@Component({
  selector: 'app-polyline-map',
  templateUrl: './polyline-map.component.html',
  styleUrls: ['./polyline-map.component.scss'],
})
export class PolylineMapComponent implements OnChanges {
  @Input() polyline: string;
  @Input() type = 'Other';
  @Input() width = '40vw';
  @Input() height = '100px';
  @Input() mapId = 'ol-map';
  @Input() start;
  @Input() end;
  @Input() showBaseLayer = false;
  @Input() layer = 'road';
  @Input() padding;
  @Input() color;

  map;
  vectorLayer;
  styles;
  colors = {
    Run: '#db7700',
    VirtualRun: '#db7700',
    Ride: '#16b800',
    VirtualRide: '#16b800',
    Swim: '#2a7efc',
    Walk: 'darkgreen',
    SnowShow: 'lime',
    Golf: 'lime',
    Soccer: 'lime',
    Runnergram: '#DE3163',
  };

  layersMap = {
    default:
      'https://c.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}@4x.png',
    road: 'https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}&scale=2',
    satellite: 'https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}&scale=2',
    hybrid: 'https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}&scale=2',
    terrain: 'https://mt1.google.com/vt/lyrs=p&x={x}&y={y}&z={z}&scale=2',
    dark: 'https://c.basemaps.cartocdn.com/rastertiles/dark_all/{z}/{x}/{y}@4x.png',
    light:
      'https://c.basemaps.cartocdn.com/rastertiles/light_all/{z}/{x}/{y}@4x.png',
  };
  constructor() {}

  ngOnChanges() {
    this.styles = {
      route: new Style({
        stroke: new Stroke({
          width: 2,
          color: this.color
            ? this.color
            : this.colors[this.type] ?? 'rgba(0,0,0,0.5)',
        }),
      }),
      line: new Style({
        stroke: new Stroke({
          width: 2,
          color: this.color ?? 'rgba(252, 76, 2,1)',
        }),
      }),
      iconend: new Style({
        image: new CircleStyle({
          radius: 5,
          fill: new Fill({ color: 'red' }),
          stroke: new Stroke({ color: 'white', width: 1 }),
        }),
      }),
    };
    setTimeout(() => {
      if (!this.map) {
        this.generateMap();
      } else {
        this.map
          .getLayers()
          .getArray()[1]
          .setStyle((feature) => this.styles[feature.get('type')]);
        if (this.layersMap[this.layer]) {
          this.map.getLayers().getArray()[0].setVisible(true);
          this.map
            .getLayers()
            .getArray()[0]
            .getSource()
            .setUrl(this.layersMap[this.layer]);
        } else {
          this.map.getLayers().getArray()[0].setVisible(false);
        }
      }
    });
  }

  generateMap() {
    const route = new Polyline({
      factor: 1e5,
    }).readGeometry(this.polyline, {
      dataProjection: 'EPSG:4326',
      featureProjection: 'EPSG:3857',
    });
    const routeFeature = new Feature({
      type: 'route',
      geometry: route,
    });
    const vectorLayer = new VectorLayer({
      source: new VectorSource({
        features: [routeFeature],
      }),
      style: (feature) => this.styles[feature.get('type')],
    });
    const previewLine = new Feature({
      geometry: new LineString([]),
    });
    const previewVector = new VectorLayer({
      source: new VectorSource({
        features: [previewLine],
      }),
      style: new Style({
        stroke: new Stroke({
          color: this.color ?? 'rgba(252, 76, 2, 1)',
          width: 2,
        }),
      }),
    });
    const myLayers: any = [vectorLayer, previewVector];

    if (this.start && this.end) {
      const startPoint = routeFeature
        .getGeometry()
        .getClosestPoint(fromLonLat([this.start[1], this.start[0]]));
      const endPoint = routeFeature
        .getGeometry()
        .getClosestPoint(fromLonLat([this.end[1], this.end[0]]));
      const previewCoords = this.getPartialRingCoords(
        routeFeature,
        startPoint,
        endPoint
      );
      previewLine.getGeometry().setCoordinates(previewCoords);
    }
    if (this.showBaseLayer || (this.start && this.end)) {
      const baseLayer = new TileLayer({
        source: new XYZ({
          url: this.layersMap[this.layer],
          tileSize: 256,
          crossOrigin: 'anonymous',
        }),
      });
      myLayers.unshift(baseLayer);
      // console.log('loadingMap')
    }

    this.map = new Map({
      view: new View({
        center: route.getExtent(),
        showFullExtent: true,
      }),
      layers: myLayers,
      controls: [],
      target: this.mapId,
    });
    setTimeout(() => {
      let padding = [0, 0, 0, 0];
      this.map.updateSize();
      window.dispatchEvent(new Event('resize'));
      if (this.start && this.end) {
        padding = [30, 10, 30, 70];
        this.map
          .getView()
          .fit(previewLine.getGeometry().getExtent(), { padding });
      } else {
        if (this.showBaseLayer && !this.padding) {
          padding = [5, 5, 5, 5];
        } else {
          padding = this.padding;
        }
        this.map.getView().fit(route.getExtent(), { padding });
      }
    }, 1000);

  }

  private getPartialRingCoords(feature, startPoint, endPoint) {
    let polygon = feature.getGeometry();
    if (polygon.getType() === 'MultiPolygon') {
      polygon = polygon.getPolygon(0);
    }
    const ringCoords = polygon.getCoordinates();
    let i;
    let pointA;
    let pointB;
    let startSegmentIndex = -1;
    for (i = 0; i < ringCoords.length; i++) {
      pointA = ringCoords[i];
      pointB = ringCoords[this.mod(i + 1, ringCoords.length)];
      // check if this is the start segment dot product
      if (this.isOnSegment(startPoint, pointA, pointB)) {
        startSegmentIndex = i;
        break;
      }
    }
    const cwCoordinates = [];
    let cwLength = 0;
    const ccwCoordinates = [];
    let ccwLength = 0;
    // build clockwise coordinates
    for (i = 0; i < ringCoords.length; i++) {
      pointA =
        i === 0
          ? startPoint
          : ringCoords[this.mod(i + startSegmentIndex, ringCoords.length)];
      pointB =
        ringCoords[this.mod(i + startSegmentIndex + 1, ringCoords.length)];
      cwCoordinates.push(pointA);
      if (this.isOnSegment(endPoint, pointA, pointB)) {
        cwCoordinates.push(endPoint);
        cwLength += this.length(pointA, endPoint);
        break;
      } else {
        cwLength += this.length(pointA, pointB);
      }
    }
    // build counter-clockwise coordinates
    for (i = 0; i < ringCoords.length; i++) {
      pointA = ringCoords[this.mod(startSegmentIndex - i, ringCoords.length)];
      pointB =
        i === 0
          ? startPoint
          : ringCoords[this.mod(startSegmentIndex - i + 1, ringCoords.length)];
      ccwCoordinates.push(pointB);
      if (this.isOnSegment(endPoint, pointA, pointB)) {
        ccwCoordinates.push(endPoint);
        ccwLength += this.length(endPoint, pointB);
        break;
      } else {
        ccwLength += this.length(pointA, pointB);
      }
    }
    // keep the shortest path
    return ccwLength < cwLength ? ccwCoordinates : cwCoordinates;
  }

  private length(a, b) {
    return Math.sqrt(
      (b[0] - a[0]) * (b[0] - a[0]) + (b[1] - a[1]) * (b[1] - a[1])
    );
  }

  private isOnSegment(c, a, b) {
    const lengthAc = this.length(a, c);
    const lengthAb = this.length(a, b);
    const dot =
      ((c[0] - a[0]) * (b[0] - a[0]) + (c[1] - a[1]) * (b[1] - a[1])) /
      lengthAb;
    return Math.abs(lengthAc - dot) < 1e-6 && lengthAc < lengthAb;
  }

  private mod(a, b) {
    return ((a % b) + b) % b;
  }
}
